import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { relazioniAPI, clientsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RelazioneFormDialog } from '../components/relazioni/RelazioneFormDialog';
import { RelazioneDetailDialog } from '../components/relazioni/RelazioneDetailDialog';
import { Plus, Search, Loader2, NotebookPen, MapPin, CalendarDays, Paperclip, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import COUNTRY_COORDS from '../lib/countryCoords';

const fmtDate = (v) => v ? new Date(v).toLocaleDateString('it-IT') : null;

export default function RelazioniPage() {
    const { user, isAdmin, canViewTeam } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [prefill, setPrefill] = useState(null);
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [country, setCountry] = useState('all');
    const [author, setAuthor] = useState('all');
    const [year, setYear] = useState('all');
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null);

    const canCreate = ['admin', 'commerciale', 'area_manager'].includes(user?.role);
    const canEditItem = (r) => isAdmin() || r.author_id === user?.id;

    const load = async () => {
        setLoading(true);
        try {
            const [rels, cls] = await Promise.all([relazioniAPI.getAll(), clientsAPI.getAll()]);
            setItems(rels.data || []);
            setClients(cls.data || []);
            if (canViewTeam()) {
                try { const s = await usersAPI.getSellers(); setSellers((s.data || []).filter(u => u.role !== 'addetto_consegna')); } catch { /* noop */ }
            }
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Errore nel caricamento delle relazioni'));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apertura precompilata dal Calendario / scheda cliente ("Scrivi relazione") o apertura diretta di una relazione
    useEffect(() => {
        const pf = location.state?.prefill;
        const openId = location.state?.openId;
        if (pf && canCreate) {
            setPrefill(pf);
            setEditing(null);
            setFormOpen(true);
            navigate(location.pathname, { replace: true, state: null });
        } else if (openId) {
            relazioniAPI.get(openId).then(r => setDetail(r.data)).catch(() => toast.error('Relazione non trovata'));
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const countries = useMemo(() => {
        const set = new Set(Object.values(COUNTRY_COORDS).map(c => c.label));
        clients.forEach(c => { if (c.country?.trim()) set.add(c.country.trim()); });
        items.forEach(r => { if (r.country) set.add(r.country); });
        return [...set].sort((a, b) => a.localeCompare(b, 'it'));
    }, [clients, items]);

    const relYear = (r) => { const d = r.date_from || r.created_at; return d ? String(new Date(d).getFullYear()) : null; };
    const years = useMemo(() => [...new Set(items.map(relYear).filter(Boolean))].sort((a, b) => b - a), [items]);

    const filtered = useMemo(() => {
        const t = search.trim().toLowerCase();
        return items.filter(r => {
            if (country !== 'all' && r.country !== country) return false;
            if (author !== 'all' && r.author_id !== author) return false;
            if (year !== 'all' && relYear(r) !== year) return false;
            if (!t) return true;
            return [r.title, r.country, r.author_name, r.analisi_commerciale, r.analisi_tecnica, r.analisi_amministrativa, ...(r.clients || []).map(c => c.company_name)]
                .some(v => (v || '').toLowerCase().includes(t));
        }).sort((a, b) => (a.country || '').localeCompare(b.country || '', 'it') || new Date(b.date_from || b.created_at) - new Date(a.date_from || a.created_at));
    }, [items, search, country, author, year]);

    // Raggruppamento per nazione (ordine alfabetico)
    const grouped = useMemo(() => {
        const m = new Map();
        filtered.forEach(r => { const k = r.country || '—'; if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
        return [...m.entries()];
    }, [filtered]);

    const upsert = (saved) => {
        setItems(prev => {
            const i = prev.findIndex(x => x.id === saved.id);
            if (i === -1) return [saved, ...prev];
            const next = [...prev]; next[i] = saved; return next;
        });
        if (detail?.id === saved.id) setDetail(saved);
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="relazioni-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold uppercase">Relazioni</h1>
                    <p className="text-muted-foreground">Verbali e relazioni di viaggio dei commerciali · {items.length} totali</p>
                </div>
                {canCreate && (
                    <Button className="btn-primary font-heading" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-relazione-btn">
                        <Plus size={20} className="mr-2" /> NUOVA RELAZIONE
                    </Button>
                )}
            </div>

            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input placeholder="Cerca per titolo, cliente, nazione o contenuto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="relazioni-search" />
                </div>
                <Select value={year} onValueChange={setYear}>
                    <SelectTrigger className="md:w-40" data-testid="relazioni-filter-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutti gli anni</SelectItem>
                        {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="md:w-56" data-testid="relazioni-filter-country"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value="all">Tutte le nazioni</SelectItem>
                        {[...new Set(items.map(r => r.country))].sort().map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>
                {canViewTeam() && sellers.length > 0 && (
                    <Select value={author} onValueChange={setAuthor}>
                        <SelectTrigger className="md:w-56" data-testid="relazioni-filter-author"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">
                            <SelectItem value="all">Tutti i commerciali</SelectItem>
                            {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : filtered.length === 0 ? (
                <Card><CardContent className="py-16 text-center text-muted-foreground">
                    <NotebookPen className="mx-auto h-12 w-12 mb-3 opacity-20" />
                    <p data-testid="relazioni-empty">{items.length === 0 ? 'Nessuna relazione ancora. Crea la prima con "Nuova relazione".' : 'Nessuna relazione corrisponde ai filtri.'}</p>
                </CardContent></Card>
            ) : (
                <div className="space-y-6">
                    {grouped.map(([nation, list]) => (
                    <section key={nation} data-testid={`relazioni-group-${nation}`}>
                        <div className="flex items-center gap-3 mb-3">
                            <h2 className="font-heading uppercase text-lg flex items-center gap-2"><MapPin size={16} className="text-primary" /> {nation}</h2>
                            <Badge variant="outline">{list.length}</Badge>
                            <div className="flex-1 h-px bg-border" />
                        </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {list.map(r => (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => setDetail(r)}
                            className="text-left bg-white border rounded-sm p-4 hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] flex flex-col gap-2"
                            data-testid={`relazione-card-${r.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <h3 className="font-heading uppercase text-base leading-tight line-clamp-2">{r.title}</h3>
                                <Badge variant="outline" className="shrink-0 gap-1"><MapPin size={11} /> {r.country}</Badge>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                {(r.date_from || r.date_to) && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(r.date_from) || '…'} → {fmtDate(r.date_to) || '…'}</span>}
                                <span>{r.author_name}</span>
                                {r.attachments_count > 0 && <span className="inline-flex items-center gap-1"><Paperclip size={12} /> {r.attachments_count}</span>}
                            </div>
                            {r.clients?.length > 0 && (
                                <div className="text-xs text-muted-foreground inline-flex items-start gap-1">
                                    <Building2 size={12} className="mt-0.5 shrink-0" />
                                    <span className="line-clamp-1">{r.clients.map(c => c.company_name).join(', ')}</span>
                                </div>
                            )}
                            {r.analisi_commerciale?.trim() && <p className="text-sm text-muted-foreground line-clamp-3">{r.analisi_commerciale}</p>}
                            <span className="text-[11px] text-muted-foreground mt-auto">creata il {fmtDate(r.created_at)}</span>
                        </button>
                    ))}
                </div>
                    </section>
                    ))}
                </div>
            )}

            <RelazioneFormDialog
                open={formOpen}
                onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditing(null); setPrefill(null); } }}
                relazione={editing}
                prefill={prefill}
                clients={clients}
                countries={countries}
                onSaved={upsert}
            />
            <RelazioneDetailDialog
                relazione={detail}
                open={!!detail}
                onOpenChange={(v) => { if (!v) setDetail(null); }}
                canEdit={detail ? canEditItem(detail) : false}
                onEdit={(r) => { setDetail(null); setEditing(r); setFormOpen(true); }}
                onDeleted={(id) => setItems(prev => prev.filter(x => x.id !== id))}
            />
        </div>
    );
}
